/**
 * SCM12 — RFQ-response processing pipeline.
 *
 * Stitches the existing pieces together:
 *
 *   uploaded SiteDocument
 *      ↓
 *   classifyRfqResponse (SCM12 — rfqClassifier.ts)
 *      ↓ (when isRfqResponse + confidence ≥ threshold)
 *   extractQuoteFromPdf (SCM8 — extractQuoteFromPdf.ts)
 *      ↓
 *   quoteFromExtracted (SCM8)
 *      ↓
 *   linkQuoteAcrossBomItems (SCM8 — linkQuoteToBom.ts)
 *      ↓
 *   linkDocumentToBomItems (SCM8 — bidirectional back-pointer)
 *      ↓
 *   markBomItemsForReview (NEW) — flips a "quote available — review" flag
 *   on the affected BoM items when the extracted unit cost / lead time
 *   is *materially* better than the current industry-baseline values.
 *
 * Pure functions only. No Firestore writes, no network — the caller
 * (documentUpload.ts on the client; a future Cloud Function on the
 * server) wires the persistence + the notification.
 *
 * BoM-item matching strategy
 * --------------------------
 *   The classifier extracts an RFQ id when present, but we don't yet
 *   carry an `rfqId → bomItemIds` map on the plan (SCM11 builds out the
 *   RFQ outbound side; the inbound side currently relies on supplier-id
 *   and an explicit caller-supplied BoM-item list). To keep this pack
 *   self-contained, we accept the caller's `targetBomItemIds` list as
 *   an explicit input — typically `document.linkedBomItemIds` after the
 *   user multi-selected at upload time. When empty, we fall back to
 *   `inferTargetBomItems` which heuristically matches by supplier-id
 *   (when the extractor surfaced a vendor template fingerprint) and by
 *   substring match on the BoM-item name appearing anywhere in the doc
 *   body. Either matcher returns "no match" → the pipeline emits the
 *   Quote as orphan and the UI prompts the user to multi-select.
 *
 * "Materially better" thresholds
 * ------------------------------
 *   - costThresholdPct  — default 5%: a quote that's ≥5% cheaper than
 *     the current `bomItem.unitCostUsd` flips the review flag.
 *   - leadTimeThresholdPct — default 10%: a quote with lead time ≥10%
 *     shorter than the current `bomItem.leadTimeMonthsHigh` flips it.
 *   Both thresholds are configurable on the function call so the
 *   integration agent can A/B them without touching this file.
 */
import { extractQuoteFromPdf, quoteFromExtracted, type QuoteExtractionResult } from './extractQuoteFromPdf';
import {
  linkDocumentToBomItems,
  linkQuoteAcrossBomItems,
} from './linkQuoteToBom';
import {
  classifyRfqResponse,
  shouldAutoRouteAsRfqResponse,
  type RfqClassification,
} from './rfqClassifier';
import type { SiteDocument } from '../../types/siteRag';
import type {
  BomItem,
  Quote,
  SupplyChainPlan,
} from '../../types/supplyChain';

/** Per-BoM-item review flag — written into BomItem.notes prefix when set
 *  so the surface stays stable across schema bumps. The presence of the
 *  flag, NOT the magnitude, is what the BoM panel renders as a badge —
 *  the magnitude lives on the linked Quote.
 */
export const QUOTE_AVAILABLE_BADGE_MARKER = '[QUOTE-AVAILABLE]';

export interface ProcessRfqResponseOptions {
  /**
   * Caller-supplied receivedAt timestamp (ISO). When omitted, defaults to
   * `document.uploadedAt`. Pinned by tests to keep snapshots stable.
   */
  receivedAt?: string;
  /**
   * Optional fixed `now` for the extractor's per-field provenance stamps.
   * Forwarded straight to `extractQuoteFromPdf`.
   */
  now?: () => string;
  /**
   * Explicit BoM-item targets. When provided we skip inference.
   */
  targetBomItemIds?: string[];
  /**
   * Supplier id to attribute the quote to. When omitted, `inferSupplierId`
   * picks the first supplier whose name matches a vendor fingerprint.
   * Falls back to a stable `unknown-supplier` sentinel when no match —
   * the user can re-assign on the Quotes tab.
   */
  supplierId?: string;
  /**
   * Auto-route confidence threshold. Defaults to the classifier's
   * `RFQ_AUTO_ROUTE_CONFIDENCE_THRESHOLD` (0.5). Set to 1 to require
   * a perfect-match classification.
   */
  autoRouteThreshold?: number;
  /** Quote id factory — defaults to a deterministic timestamp+nonce. */
  quoteIdForItem?: (bomItemId: string) => string;
  /** "Materially cheaper" threshold (fractional). Default 0.05 = 5%. */
  costThresholdPct?: number;
  /** "Materially shorter lead time" threshold (fractional). Default 0.10 = 10%. */
  leadTimeThresholdPct?: number;
}

/** Pipeline outcome — exposes the new plan, the new quotes, the updated
 *  document, and the classifier verdict so the caller can persist or
 *  surface "review and confirm" affordances. */
export interface ProcessRfqResponseResult {
  /**
   * Final outcome:
   *   - `'auto-routed'`: classifier said yes AND we created at least one Quote.
   *   - `'orphan-quote'`: classifier said yes but no BoM target matched; the
   *     extractor still produced a Quote — caller should park it pending
   *     manual BoM selection.
   *   - `'classified-only'`: classifier said yes but extraction yielded no
   *     usable fields (no price + no lead time).
   *   - `'rejected'`: classifier said no — caller should leave the document
   *     in the regular Documents tab without spinning up a Quote.
   */
  status: 'auto-routed' | 'orphan-quote' | 'classified-only' | 'rejected';
  /** Verdict + per-signal breakdown. Always returned. */
  classification: RfqClassification;
  /** Extraction result — populated when classifier said yes. */
  extraction?: QuoteExtractionResult;
  /** Plan with the new quotes appended + BoM items flag-stamped. */
  plan: SupplyChainPlan;
  /** Document with linkedBomItemIds back-pointers updated. */
  document: SiteDocument;
  /** Quotes that were created on this pipeline run. */
  newQuotes: Quote[];
  /** BoM-item IDs that picked up the review badge on this run. */
  flaggedBomItemIds: string[];
  /** Notification payload the caller can hand to the UI / toast layer. */
  notification?: {
    title: string;
    message: string;
    /** ISO timestamp the pipeline finished. */
    at: string;
  };
}

/**
 * Top-level entry — the upload pipeline calls this after the document
 * has been processed (text extracted, summary built, tags assigned).
 * Returns a `ProcessRfqResponseResult` describing what happened; the
 * caller decides whether to persist the new plan and document.
 */
export async function processRfqResponse(
  document: SiteDocument,
  plan: SupplyChainPlan,
  options: ProcessRfqResponseOptions = {},
): Promise<ProcessRfqResponseResult> {
  const at = (options.now ?? ((): string => new Date().toISOString()))();
  const classification = classifyRfqResponse(document.textContent ?? '', {
    fileName: document.fileName,
  });

  // ── Classifier said no — bail out cleanly ────────────────────────
  if (!shouldAutoRouteAsRfqResponse(classification, options.autoRouteThreshold)) {
    return {
      status: 'rejected',
      classification,
      plan,
      document,
      newQuotes: [],
      flaggedBomItemIds: [],
    };
  }

  // ── Extraction ──────────────────────────────────────────────────
  const extraction = await extractQuoteFromPdf(document.textContent ?? '', {
    now: options.now,
  });
  const { unitCostUsd, leadTimeMonths } = extraction.fields;
  const noUsefulFields =
    (unitCostUsd == null || !Number.isFinite(unitCostUsd)) &&
    (leadTimeMonths == null || !Number.isFinite(leadTimeMonths));
  if (noUsefulFields) {
    return {
      status: 'classified-only',
      classification,
      extraction,
      plan,
      document,
      newQuotes: [],
      flaggedBomItemIds: [],
      notification: {
        title: 'RFQ response detected — needs manual review',
        message:
          `"${document.fileName}" looks like a vendor RFQ response but the extractor could not pull a price or lead time. Open the Quotes tab to enter values manually.`,
        at,
      },
    };
  }

  // ── BoM-item matching ───────────────────────────────────────────
  const supplierId =
    options.supplierId ?? inferSupplierId(plan, document.textContent ?? '');
  const explicitTargets = options.targetBomItemIds ?? document.linkedBomItemIds ?? [];
  const targetBomItemIds =
    explicitTargets.length > 0
      ? explicitTargets
      : inferTargetBomItems(plan, document, supplierId);

  // ── Build the quote(s) ──────────────────────────────────────────
  const receivedAt = options.receivedAt ?? document.uploadedAt ?? at;
  const baseQuote = quoteFromExtracted(extraction.fields, {
    id: 'tpl', // placeholder — replaced per BoM-item by linkQuoteAcrossBomItems
    bomItemId: 'tpl',
    supplierId,
    documentId: document.id,
    receivedAt,
  });
  const { id: _droppedId, bomItemId: _droppedBomId, ...quoteTemplate } = baseQuote;
  void _droppedId;
  void _droppedBomId;

  const idFactory =
    options.quoteIdForItem
    ?? ((bomItemId: string): string =>
      `q-${document.id}-${bomItemId}-${at.replace(/[^0-9]/g, '').slice(0, 14)}`);

  // ── Orphan-quote path: classifier hit but no BoM target ─────────
  if (targetBomItemIds.length === 0) {
    const orphan: Quote = {
      ...quoteTemplate,
      id: idFactory('orphan'),
      bomItemId: '', // empty = needs manual BoM-item assignment
    };
    return {
      status: 'orphan-quote',
      classification,
      extraction,
      plan: { ...plan, quotes: [...plan.quotes, orphan] },
      document,
      newQuotes: [orphan],
      flaggedBomItemIds: [],
      notification: {
        title: 'RFQ response detected — assign to a BoM item',
        message:
          `"${document.fileName}" was auto-classified as a vendor RFQ response but no matching BoM item was found. Open the Quotes tab to select the BoM line it covers.`,
        at,
      },
    };
  }

  // ── Auto-route — create one Quote per BoM item + flip flags ─────
  const { plan: planWithQuotes, newQuotes } = linkQuoteAcrossBomItems(
    plan,
    quoteTemplate,
    targetBomItemIds,
    idFactory,
  );
  const updatedDocument = linkDocumentToBomItems(document, targetBomItemIds);

  // Apply "quote available" badge on BoM items where the extracted
  // values materially beat the baseline. The badge sticks on every
  // matched item — even when the quote isn't materially better — so
  // reviewers can audit silently-rejected baselines. The threshold
  // controls only whether the badge is "MATERIAL" vs plain.
  const costThreshold = options.costThresholdPct ?? 0.05;
  const leadThreshold = options.leadTimeThresholdPct ?? 0.10;
  const { bom: bomWithFlags, flaggedIds } = flagBomItemsForReview(
    planWithQuotes.bom,
    targetBomItemIds,
    {
      extractedUnitCostUsd: unitCostUsd ?? null,
      extractedLeadTimeMonths: leadTimeMonths ?? null,
      costThresholdPct: costThreshold,
      leadTimeThresholdPct: leadThreshold,
    },
  );

  const finalPlan: SupplyChainPlan = {
    ...planWithQuotes,
    bom: bomWithFlags,
    updatedAt: at,
  };

  const notification = buildAutoRoutedNotification(
    document,
    classification,
    newQuotes,
    flaggedIds,
    at,
  );

  return {
    status: 'auto-routed',
    classification,
    extraction,
    plan: finalPlan,
    document: updatedDocument,
    newQuotes,
    flaggedBomItemIds: flaggedIds,
    notification,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pure helper — apply / clear the QUOTE_AVAILABLE_BADGE_MARKER on the
 * supplied BoM items. Returns a new BoM array + the list of ids whose
 * marker actually flipped on this call.
 *
 * Rule: badge ON when the extracted quote is materially better than the
 * current item values; OFF otherwise. The canonical state lives on
 * `bomItem.reviewFlag`; the notes-prefix marker is still mirrored for
 * backward compatibility with older readers.
 *
 * "Materially better" =
 *   unit cost ≤ (1 - costThresholdPct) × current OR
 *   lead time high ≤ (1 - leadTimeThresholdPct) × current
 */
export function flagBomItemsForReview(
  bom: BomItem[],
  bomItemIds: string[],
  opts: {
    extractedUnitCostUsd: number | null;
    extractedLeadTimeMonths: number | null;
    costThresholdPct: number;
    leadTimeThresholdPct: number;
  },
): { bom: BomItem[]; flaggedIds: string[] } {
  const targets = new Set(bomItemIds);
  const flaggedIds: string[] = [];
  const out = bom.map((item) => {
    if (!targets.has(item.id)) return item;
    const better = isMateriallyBetter(item, opts);
    const currentNotes = item.notes ?? '';
    const hasBadge = currentNotes.startsWith(QUOTE_AVAILABLE_BADGE_MARKER);
    if (better && !hasBadge) {
      flaggedIds.push(item.id);
      return {
        ...item,
        reviewFlag: 'quote-available' as const,
        notes: currentNotes
          ? `${QUOTE_AVAILABLE_BADGE_MARKER} ${currentNotes}`
          : QUOTE_AVAILABLE_BADGE_MARKER,
      };
    }
    if (!better && (item.reviewFlag === 'quote-available' || hasBadge)) {
      const stripped = hasBadge
        ? currentNotes.slice(QUOTE_AVAILABLE_BADGE_MARKER.length).replace(/^\s+/, '')
        : currentNotes;
      return {
        ...item,
        reviewFlag: undefined,
        notes: stripped.length > 0 ? stripped : undefined,
      };
    }
    return item;
  });
  return { bom: out, flaggedIds };
}

/**
 * Pure helper — strip the QUOTE_AVAILABLE_BADGE_MARKER from a BoM item's
 * notes. Called by the caller when the user awards / rejects the quote
 * so the badge clears on the BoM panel.
 */
export function clearReviewBadge(item: BomItem): BomItem {
  const currentNotes = item.notes ?? '';
  if (item.reviewFlag !== 'quote-available' && !currentNotes.startsWith(QUOTE_AVAILABLE_BADGE_MARKER)) return item;
  const stripped = currentNotes.startsWith(QUOTE_AVAILABLE_BADGE_MARKER)
    ? currentNotes.slice(QUOTE_AVAILABLE_BADGE_MARKER.length).replace(/^\s+/, '')
    : currentNotes;
  return {
    ...item,
    reviewFlag: undefined,
    notes: stripped.length > 0 ? stripped : undefined,
  };
}

/**
 * "Materially better" checker. Returns true when EITHER the extracted
 * unit cost is ≥ costThresholdPct cheaper than the current value OR the
 * extracted lead time is ≥ leadTimeThresholdPct shorter than the current
 * high band.
 */
export function isMateriallyBetter(
  item: BomItem,
  opts: {
    extractedUnitCostUsd: number | null;
    extractedLeadTimeMonths: number | null;
    costThresholdPct: number;
    leadTimeThresholdPct: number;
  },
): boolean {
  // Cost check
  if (
    opts.extractedUnitCostUsd != null &&
    Number.isFinite(opts.extractedUnitCostUsd) &&
    item.unitCostUsd != null &&
    Number.isFinite(item.unitCostUsd) &&
    item.unitCostUsd > 0
  ) {
    const fractionalDelta = (item.unitCostUsd - opts.extractedUnitCostUsd) / item.unitCostUsd;
    if (fractionalDelta >= opts.costThresholdPct) return true;
  }
  // Lead time check
  if (
    opts.extractedLeadTimeMonths != null &&
    Number.isFinite(opts.extractedLeadTimeMonths) &&
    Number.isFinite(item.leadTimeMonthsHigh) &&
    item.leadTimeMonthsHigh > 0
  ) {
    const fractionalDelta =
      (item.leadTimeMonthsHigh - opts.extractedLeadTimeMonths) / item.leadTimeMonthsHigh;
    if (fractionalDelta >= opts.leadTimeThresholdPct) return true;
  }
  return false;
}

/**
 * Heuristic supplier matcher — find the first supplier whose name
 * appears in the document body. Falls back to `unknown-supplier` when
 * no plan supplier matches. The user re-assigns from the Quotes tab.
 */
function inferSupplierId(plan: SupplyChainPlan, text: string): string {
  if (!text || plan.suppliers.length === 0) return 'unknown-supplier';
  const lower = text.toLowerCase();
  const hit = plan.suppliers.find((s) => lower.includes(s.name.toLowerCase()));
  return hit?.id ?? 'unknown-supplier';
}

/**
 * Heuristic BoM-item matcher — looks for the BoM item name as a
 * substring (case-insensitive) inside the document body. Returns the
 * matching BoM-item ids; an empty array means no BoM match → orphan
 * quote. Filters out items whose names are <4 chars to avoid spurious
 * matches on common words.
 */
export function inferTargetBomItems(
  plan: SupplyChainPlan,
  document: SiteDocument,
  supplierId: string,
): string[] {
  const text = (document.textContent ?? '').toLowerCase();
  if (!text) return [];
  const matches = plan.bom.filter((item) => {
    if (item.name.trim().length < 4) return false;
    if (!text.includes(item.name.toLowerCase())) return false;
    // When we have a real supplier match, prefer items already on the
    // supplier's shortlist (when populated) to avoid attaching a
    // Hitachi quote to an item that's only ever been quoted by Siemens.
    if (supplierId !== 'unknown-supplier' && item.shortlistedSupplierIds) {
      if (!item.shortlistedSupplierIds.includes(supplierId)) return false;
    }
    return true;
  });
  return matches.map((m) => m.id);
}

function buildAutoRoutedNotification(
  document: SiteDocument,
  classification: RfqClassification,
  newQuotes: Quote[],
  flaggedIds: string[],
  at: string,
): ProcessRfqResponseResult['notification'] {
  const quoteCount = newQuotes.length;
  const conf = (classification.confidence * 100).toFixed(0);
  const flaggedSegment =
    flaggedIds.length > 0
      ? ` ${flaggedIds.length} BoM item${flaggedIds.length === 1 ? '' : 's'} flagged for review.`
      : '';
  return {
    title: `Vendor quote auto-extracted (${conf}% confidence)`,
    message:
      `"${document.fileName}" → ${quoteCount} quote${quoteCount === 1 ? '' : 's'} added to the Quotes tab.${flaggedSegment}`,
    at,
  };
}

export const __test__ = {
  inferSupplierId,
  buildAutoRoutedNotification,
};
